import type { D9Planet, NavamsaResponse } from "@/lib/api/navamsa";

import { degreeInSign, nakshatraPada } from "../_placement";

type Language = "en" | "hi";

export interface D9TableCopy {
  readonly tableTitle: string;
  readonly headers: readonly string[];
  readonly lagnaRow: string;
  readonly vargottamaBadge: string;
  readonly pushkaraBadge: string;
  readonly flagsNote: string;
}

interface Row {
  readonly id: string;
  readonly planet: string;
  readonly secondary?: string;
  readonly d1Rashi: string;
  readonly d1Longitude?: number | null;
  readonly nakshatra?: string | null;
  readonly pada?: number | null;
  readonly d9Rashi: string;
  readonly d9House: number;
  readonly dignity?: { readonly tone: keyof typeof DIGNITY_TONE; readonly label: string };
  readonly vargottama: boolean;
  readonly pushkara: boolean;
}

const DIGNITY_TONE = {
  exalted: "text-success",
  own_sign: "text-success",
  debilitated: "text-error",
} as const;

/** D1-against-D9 placement table: the lagna first, then each graha, with its flags. */
export default function D9PlacementTable({
  result,
  copy,
  language,
}: {
  result: NavamsaResponse;
  copy: D9TableCopy;
  language: Language;
}) {
  const rows = [
    ...(result.lagna ? [lagnaRow(result, result.lagna, copy, language)] : []),
    ...result.d9_chart.map((planet) => planetRow(planet, language)),
  ];
  return (
    <section className="mt-6">
      <h4 className="mb-2 font-serif text-subtitle-small font-bold text-text-primary">
        {copy.tableTitle}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-body-small">
          <thead>
            <tr className="border-b border-border-default text-left text-caption font-semibold uppercase tracking-eyebrow text-intent-brand">
              {copy.headers.map((header, index) => (
                <th
                  key={header}
                  className={index === copy.headers.length - 1 ? "py-2" : "py-2 pr-3"}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <PlacementRow key={row.id} row={row} copy={copy} language={language} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-caption leading-relaxed text-text-muted">{copy.flagsNote}</p>
    </section>
  );
}

function lagnaRow(
  result: NavamsaResponse,
  lagna: NonNullable<NavamsaResponse["lagna"]>,
  copy: D9TableCopy,
  language: Language
): Row {
  const isHi = language === "hi";
  return {
    id: "lagna",
    planet: copy.lagnaRow,
    d1Rashi: isHi ? lagna.d1_rashi_display_hi : lagna.d1_rashi_display_en,
    d1Longitude: lagna.d1_longitude,
    nakshatra: lagna.nakshatra,
    pada: lagna.nakshatra_pada,
    d9Rashi: isHi ? result.navamsa_lagna_display_hi : result.navamsa_lagna_display_en,
    d9House: 1,
    vargottama: lagna.is_vargottama,
    pushkara: lagna.is_pushkara_navamsha,
  };
}

function planetRow(planet: D9Planet, language: Language): Row {
  const isHi = language === "hi";
  const dignityLabel = isHi ? planet.d9_dignity_display_hi : planet.d9_dignity_display_en;
  return {
    id: planet.planet_id,
    planet: isHi ? planet.planet_display_hi : planet.planet_display_en,
    secondary: isHi ? undefined : planet.planet_display_hi,
    d1Rashi: isHi ? planet.d1_rashi_display_hi : planet.d1_rashi_display_en,
    d1Longitude: planet.d1_longitude,
    nakshatra: planet.nakshatra,
    pada: planet.nakshatra_pada,
    d9Rashi: isHi ? planet.d9_rashi_display_hi : planet.d9_rashi_display_en,
    d9House: planet.d9_house,
    dignity:
      planet.d9_dignity && dignityLabel
        ? { tone: planet.d9_dignity, label: dignityLabel }
        : undefined,
    vargottama: planet.is_vargottama,
    pushkara: Boolean(planet.is_pushkara_navamsha),
  };
}

function PlacementRow({
  row,
  copy,
  language,
}: {
  row: Row;
  copy: D9TableCopy;
  language: Language;
}) {
  const hasFlag = row.vargottama || row.pushkara;
  return (
    <tr
      data-row={row.id}
      className={`border-b border-border-subtle ${row.vargottama ? "font-semibold" : ""}`}
    >
      <td className="py-2 pr-3 text-text-primary">
        {row.planet}
        {row.secondary ? (
          <span className="ml-1 text-caption text-text-muted">{row.secondary}</span>
        ) : null}
      </td>
      <td className="py-2 pr-3 text-text-muted">
        <span className="block">
          {row.d1Rashi}
          {row.d1Longitude != null ? ` ${degreeInSign(row.d1Longitude)}` : ""}
        </span>
        {row.nakshatra ? (
          <span className="block text-caption">
            {nakshatraPada(row.nakshatra, row.pada, language)}
          </span>
        ) : null}
      </td>
      <td className="py-2 pr-3 text-text-primary">{row.d9Rashi}</td>
      <td className="py-2 pr-3 text-text-muted">{row.d9House}</td>
      <td className="py-2 pr-3">
        {row.dignity ? (
          <span className={`text-caption font-semibold ${DIGNITY_TONE[row.dignity.tone]}`}>
            {row.dignity.label}
          </span>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </td>
      <td className="py-2">
        {hasFlag ? (
          <span className="flex flex-wrap gap-1">
            {row.vargottama ? <FlagBadge label={copy.vargottamaBadge} strong /> : null}
            {row.pushkara ? <FlagBadge label={copy.pushkaraBadge} /> : null}
          </span>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

function FlagBadge({ label, strong = false }: { label: string; strong?: boolean }) {
  return (
    <span
      className={
        strong
          ? "inline-block rounded bg-surface-brand-strong px-1.5 py-0.5 text-caption font-bold uppercase tracking-eyebrow text-text-on-brand"
          : "inline-block rounded border border-border-interactive bg-surface-brand-subtle px-1.5 py-0.5 text-caption font-bold uppercase tracking-eyebrow text-intent-brand"
      }
    >
      {label}
    </span>
  );
}
